import { useCallback, useEffect, useState } from "react";

import { EDITABLE_CATCH_FIELDS } from "./editPostPayloadBuilder";

const buildPostWithEditDraft = (post, draftFormState) => {
  if (!draftFormState || typeof draftFormState !== "object") {
    return post;
  }

  const draftCatch =
    draftFormState.catch && typeof draftFormState.catch === "object"
      ? draftFormState.catch
      : {};
  const catchPatch = {};

  EDITABLE_CATCH_FIELDS.forEach((fieldName) => {
    if (Object.prototype.hasOwnProperty.call(draftCatch, fieldName)) {
      catchPatch[fieldName] = draftCatch[fieldName];
    }
  });

  if (typeof draftFormState.caption === "string") {
    catchPatch.notes = draftFormState.caption;
  }

  return {
    ...post,
    caption:
      typeof draftFormState.caption === "string"
        ? draftFormState.caption
        : post.caption,
    catch: {
      ...(post.catch || {}),
      ...catchPatch,
    },
  };
};

const mergeEditedPost = (currentPost, updatedPost) => {
  if (!updatedPost) return currentPost;

  return {
    ...(currentPost || {}),
    ...updatedPost,
    catch: updatedPost.catch
      ? {
          ...(currentPost?.catch || {}),
          ...updatedPost.catch,
        }
      : currentPost?.catch,
  };
};

const withEditRestoreContext = (post, comparisonPost) => ({
  ...post,
  _editComparisonPost: comparisonPost,
});

const withEditMapPick = (post, comparisonPost, coordinates, requestedAt) => ({
  ...withEditRestoreContext(post, comparisonPost),
  _mapPickedCoordinates: coordinates,
  _mapPickRequestedAt: requestedAt,
});

export default function useProfileEditPostState({
  posts,
  editPostMapSelection,
  onEditPostMapSelectionConsumed,
  onUpdatePost,
}) {
  const [editingPost, setEditingPost] = useState(null);

  const handleCloseEditPost = useCallback(() => {
    setEditingPost(null);
  }, []);

  const handleOpenEditPost = useCallback((post) => {
    setEditingPost(post);
  }, []);

  const handleEditPostSaved = useCallback(
    (updatedPost, options = {}) => {
      onUpdatePost?.(updatedPost, editingPost);
      if (options.close === false) {
        setEditingPost((current) => mergeEditedPost(current, updatedPost));
        return;
      }
      setEditingPost(null);
    },
    [editingPost, onUpdatePost]
  );

  useEffect(() => {
    if (!editPostMapSelection?.postId) return;

    const matchedPost = posts.find(
      (post) => post?.id === editPostMapSelection.postId
    );
    if (!matchedPost) return;
    const draftPost = buildPostWithEditDraft(
      matchedPost,
      editPostMapSelection.draftFormState
    );

    if (!editPostMapSelection.coordinates) {
      setEditingPost(withEditRestoreContext(draftPost, matchedPost));
      onEditPostMapSelectionConsumed?.();
      return;
    }

    setEditingPost(
      withEditMapPick(
        {
          ...draftPost,
          catch: {
            ...(draftPost.catch || {}),
            coordinates: editPostMapSelection.coordinates,
            location: "",
          },
        },
        matchedPost,
        editPostMapSelection.coordinates,
        editPostMapSelection.requestedAt
      )
    );
    onEditPostMapSelectionConsumed?.();
  }, [
    editPostMapSelection?.coordinates,
    editPostMapSelection?.draftFormState,
    editPostMapSelection?.postId,
    editPostMapSelection?.requestedAt,
    onEditPostMapSelectionConsumed,
    posts,
  ]);

  return {
    editingPost,
    setEditingPost,
    handleCloseEditPost,
    handleOpenEditPost,
    handleEditPostSaved,
  };
}
